import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, Package, PackageCheck, Truck } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError, formatDate } from "@/lib/format";
import { DeliverDialog } from "./DeliverDialog";
import { ShipDialog } from "./ShipDialog";

const STEPS = [
  { key: "unfulfilled", label: "Da spedire", icon: Package, done: "Ordine riportato a \"da spedire\"" },
  { key: "shipped", label: "Spedito", icon: Truck, done: "Ordine segnato come spedito" },
  { key: "delivered", label: "Consegnato", icon: PackageCheck, done: "Ordine segnato come consegnato" },
];

const EMAIL_TOAST = {
  shipped: (to) => `Email di spedizione in invio a ${to}`,
  delivered: (to) => `Email di ringraziamento in invio a ${to}`,
};

const FulfillmentInfo = ({ order, testid }) => {
  const shipped = order.fulfillment_status === "shipped" || order.fulfillment_status === "delivered";
  const tracking = shipped ? [order.carrier && `Corriere: ${order.carrier}`, order.tracking_number && `Tracciamento: ${order.tracking_number}`].filter(Boolean) : [];
  return (
    <div className="space-y-1 text-[14px] text-slate2" data-testid={testid}>
      {tracking.length > 0 && <p className="text-navy">{tracking.join(" · ")}</p>}
      {order.fulfillment_status === "delivered" && order.delivered_at && <p>Consegnato il {formatDate(order.delivered_at)}</p>}
      {order.shipping_email_sent_at && (
        <p className="flex items-center gap-1.5"><Mail className="h-4 w-4 shrink-0" /> Email di spedizione inviata il {formatDate(order.shipping_email_sent_at)}</p>
      )}
      {order.delivered_email_sent_at && (
        <p className="flex items-center gap-1.5"><Mail className="h-4 w-4 shrink-0" /> Email di ringraziamento inviata il {formatDate(order.delivered_email_sent_at)}</p>
      )}
    </div>
  );
};

export const FulfillmentControl = ({ order }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(null);
  const [saving, setSaving] = useState(false);
  const ref = order.session_id.slice(-8);
  const current = order.fulfillment_status ?? "unfulfilled";

  const update = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.session_id}/fulfillment`, payload);
      toast.success(STEPS.find((s) => s.key === payload.status)?.done);
      if (EMAIL_TOAST[data?.email_queued]) toast.success(EMAIL_TOAST[data.email_queued](data.customer_email));
      setOpen(null);
      await qc.invalidateQueries({ queryKey: ["admin-orders"] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const choose = (key) => {
    if (key === "shipped") setOpen("ship");
    else if (key === "delivered" && current !== "delivered") setOpen("deliver");
    else if (key !== current) update({ status: key });
  };
  const toggle = (name) => (value) => setOpen(value ? name : null);

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-testid={`fulfillment-${ref}`}>
      <div role="group" aria-label="Stato della spedizione" className="grid grid-cols-3 gap-1 rounded-full bg-sand p-1 sm:inline-grid">
        {STEPS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            disabled={saving}
            aria-pressed={current === key}
            onClick={() => choose(key)}
            data-testid={`fulfillment-${ref}-${key}`}
            className={`inline-flex h-11 items-center justify-center gap-2 rounded-full px-2 text-[14px] transition-colors disabled:opacity-60 sm:px-5 sm:text-[15px] ${current === key ? "bg-navy text-ivory" : "text-navy hover:bg-cream"}`}
          >
            <Icon className="hidden h-4 w-4 shrink-0 sm:block" /> {label}
          </button>
        ))}
      </div>
      <FulfillmentInfo order={order} testid={`fulfillment-info-${ref}`} />
      <ShipDialog order={order} open={open === "ship"} onOpenChange={toggle("ship")} onConfirm={update} saving={saving} />
      <DeliverDialog order={order} open={open === "deliver"} onOpenChange={toggle("deliver")} onConfirm={update} saving={saving} />
    </div>
  );
};
